import type {VariantClassSchedule} from "./operations/storefront";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a Shopify calendar date (`YYYY-MM-DD`) as local midnight.
 * Returns null when the string is missing, malformed, or not a real calendar day.
 */
export function parseDateOnly(iso: string): Date | null {
	const match = DATE_ONLY_PATTERN.exec(iso);
	if (!match) {
		return null;
	}

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const date = new Date(year, month - 1, day);

	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}

	return date;
}

function formatDate(iso: string, opts?: {includeYear?: boolean}): string {
	const includeYear = opts?.includeYear ?? true;
	const date = parseDateOnly(iso);
	if (!date) {
		return iso;
	}
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		...(includeYear ? {year: "numeric"} : {}),
	});
}

// converts an IANA timezone (e.g. "America/Los_Angeles") to its generic,
// DST-agnostic abbreviation (e.g. "PT"). Falls back to the raw IANA string
// if it isn't a valid timezone id.
function formatTimezoneAbbr(ianaTz: string, date: string): string {
	const parsed = parseDateOnly(date);
	if (!parsed) {
		return ianaTz;
	}
	// use try block in case the IANA is malformed (no validation on shopify)
	try {
		const parts = new Intl.DateTimeFormat("en-US", {
			timeZone: ianaTz,
			timeZoneName: "shortGeneric",
			// the API requires us to pass in a reference timestamp. This
			// matters if we want to differentiate between PST and PDT, but does
			// not matter for "shortGeneric" (PT)
		}).formatToParts(parsed);
		return parts.find((p) => p.type === "timeZoneName")?.value ?? ianaTz;
	} catch {
		return ianaTz;
	}
}

// given a time in "HH:MM" 24 hour format,
// returns a string of the form "4:30-6:00pm"
function formatTimeRange(startStr: string, endStr: string): string {
	const parse = (t: string) => {
		const parts = t.split(":");
		return {h: Number(parts[0]), m: Number(parts[1])};
	};
	const start = parse(startStr);
	const end = parse(endStr);
	const fmt = (h: number, m: number) =>
		`${h % 12 || 12}:${String(m).padStart(2, "0")}`;
	const startPeriod = start.h < 12 ? "am" : "pm";
	const endPeriod = end.h < 12 ? "am" : "pm";
	if (startPeriod === endPeriod) {
		return `${fmt(start.h, start.m)}-${fmt(end.h, end.m)}${endPeriod}`;
	}
	// only show the "am/pm" on both start and end times if they are different
	return `${fmt(start.h, start.m)}${startPeriod}-${fmt(end.h, end.m)}${endPeriod}`;
}

function formatDay(day: string): string {
	return day.charAt(0).toUpperCase() + day.slice(1, 3).toLowerCase();
}

export function formatScheduleString(
	schedule: VariantClassSchedule | null
): string | null {
	if (!schedule) return null;

	const {dateStartAt, dateEndAt, timezone, meetingTimes} = schedule;
	if (!dateStartAt || !dateEndAt) return null;

	// if same year, then omit the year completely
	const sameYear = dateStartAt.split("-")[0] === dateEndAt.split("-")[0];
	const dateRange = `${formatDate(dateStartAt, {includeYear: !sameYear})} - ${formatDate(dateEndAt, {includeYear: !sameYear})}`;

	const validMeetings = meetingTimes.filter(
		(mt) => mt.day && mt.timeStartAt && mt.timeEndAt
	);
	if (!validMeetings.length) return dateRange;

	// checked for day in validMeetings already, so we know it exists
	const days = validMeetings.map((mt) => formatDay(mt.day!)).join(", ");
	// find meetings time objects with the same start and end times
	const timeKeys = new Set(
		validMeetings.map((mt) => `${mt.timeStartAt}-${mt.timeEndAt}`)
	);

	// only include the times in the final schedule string if there is one,
	// otherwise omit the time
	if (timeKeys.size === 1) {
		const tzSuffix = timezone
			? ` ${formatTimezoneAbbr(timezone, dateStartAt)}`
			: "";
		const timeRange = formatTimeRange(
			validMeetings[0]!.timeStartAt!,
			validMeetings[0]!.timeEndAt!
		);
		return `${dateRange}, ${days} ${timeRange}${tzSuffix}`;
	}

	return `${dateRange}, ${days}`;
}
